import type { CSSProperties } from "react";
import { cn } from "@/lib/utils";

/**
 * O símbolo do Kompil se formando sem parar: o laço do infinito se desenha, o ponto aparece, e o
 * traço segue apagando pela frente. É o "carregando" de todo o sistema (botões, telas, chats de IA,
 * login). Gerado por apps/desktop/scripts/gerar-infinito.py (mesmo traço do GIF da atualização).
 */
export const INFINITY_PATH =
  "M60 30L75.89 15.43Q84 8 91.78 15.78L98.22 22.22Q106 30 98.22 37.78L91.78 44.22Q84 52 75.89 44.57L60 30L44.11 15.43Q36 8 28.22 15.78L21.78 22.22Q14 30 21.78 37.78L28.22 44.22Q36 52 44.11 44.57L60 30Z";

export function InfinityMark({
  className,
  stroke = 7,
  label = "Carregando",
  track = true,
  dot = true,
  style,
}: {
  className?: string;
  style?: CSSProperties;
  /** Espessura do traço (na caixa 120 × 60). Ícones pequenos ficam mais legíveis com traço grosso. */
  stroke?: number;
  label?: string | null;
  /** O laço inteiro bem fraco por baixo do traço. */
  track?: boolean;
  dot?: boolean;
}) {
  return (
    <svg
      viewBox="0 0 120 60"
      fill="none"
      className={cn("kompil-infinity inline-block shrink-0", className)}
      style={style}
      {...(label ? { role: "status", "aria-label": label } : { "aria-hidden": true })}
    >
      {track ? (
        <path
          d={INFINITY_PATH}
          stroke="currentColor"
          strokeOpacity={0.14}
          strokeWidth={stroke}
          strokeLinejoin="round"
        />
      ) : null}
      <path
        className="kompil-infinity-draw"
        d={INFINITY_PATH}
        pathLength={100}
        stroke="currentColor"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {dot ? <circle className="kompil-infinity-dot" cx="112.5" cy="51" r="5.5" fill="currentColor" /> : null}
    </svg>
  );
}

/**
 * Carregando com texto opcional (telas, painéis, chats de IA). `size`: largura do símbolo em px.
 */
export function InfinityLoader({
  size = 56,
  label,
  className,
}: {
  size?: number;
  label?: string;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col items-center justify-center gap-3 text-fg", className)}>
      <InfinityMark
        label={label ?? "Carregando"}
        stroke={size < 32 ? 10 : 7}
        // A altura sai da proporção (2 : 1).
        style={{ width: size, height: size / 2 }}
      />
      {label ? <p className="animate-pulse text-[12.5px] text-fg-muted">{label}</p> : null}
    </div>
  );
}

/**
 * O infinito da tela de entrada, com cara de abertura de filme: translúcido, com um brilho difuso
 * por trás, um traço que se forma devagar (e se desfaz devagar) e uma luz que percorre o laço. Tudo
 * em ciclos longos e com aceleração suave — nada rápido nem agressivo.
 */
export function CinematicInfinity({ className, style }: { className?: string; style?: CSSProperties }) {
  return (
    <svg
      viewBox="-10 -10 140 80"
      fill="none"
      aria-hidden
      className={cn("kompil-cine overflow-visible", className)}
      style={style}
    >
      <defs>
        <linearGradient id="kompil-cine-stroke" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
          <stop offset="55%" stopColor="#ffffff" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0.75" />
        </linearGradient>
        {/* Região do filtro no espaço do desenho: o brilho nunca é cortado (sem "caixa" em volta). */}
        <filter id="kompil-cine-glow" filterUnits="userSpaceOnUse" x="-10" y="-10" width="140" height="80">
          <feGaussianBlur stdDeviation="5" />
        </filter>
        <filter id="kompil-cine-soft" filterUnits="userSpaceOnUse" x="-10" y="-10" width="140" height="80">
          <feGaussianBlur stdDeviation="1.6" />
        </filter>
      </defs>
      {/* O laço inteiro, quase sumindo: a forma está sempre lá, o traço só a acende. */}
      <path d={INFINITY_PATH} stroke="#ffffff" strokeOpacity={0.07} strokeWidth={6} strokeLinejoin="round" />
      {/* Brilho difuso que respira junto com o traço. */}
      <path
        className="kompil-cine-draw"
        d={INFINITY_PATH}
        pathLength={100}
        stroke="#ffffff"
        strokeOpacity={0.38}
        strokeWidth={9}
        strokeLinecap="round"
        strokeLinejoin="round"
        filter="url(#kompil-cine-glow)"
      />
      {/* O traço principal, translúcido. */}
      <path
        className="kompil-cine-draw"
        d={INFINITY_PATH}
        pathLength={100}
        stroke="url(#kompil-cine-stroke)"
        strokeWidth={5.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Uma luz curta que percorre o laço sem parar, bem devagar. */}
      <path
        className="kompil-cine-sweep"
        d={INFINITY_PATH}
        pathLength={100}
        stroke="#ffffff"
        strokeWidth={4}
        strokeLinecap="round"
        strokeLinejoin="round"
        filter="url(#kompil-cine-soft)"
      />
      <circle className="kompil-cine-dot" cx="112.5" cy="51" r="5" fill="#ffffff" fillOpacity={0.8} />
      <circle
        className="kompil-cine-dot kompil-cine-halo"
        cx="112.5"
        cy="51"
        r="8"
        fill="#ffffff"
        filter="url(#kompil-cine-glow)"
      />
    </svg>
  );
}
